<template>
  <div :class="componentClasses">
    <div class="ask-message-avatar">
      <AskIcon v-if="message.role === 'assistant'" />

      <div
        v-else
        class="ask-user-avatar"
        :title="formatDate(message.timestamp)"
      >
        <img
          v-if="advisorStore.advisor?.firm.logo"
          :src="advisorStore.advisor?.firm.logo"
          alt="Your logo"
          height="36"
          width="36"
        >
        <span
          v-else
        >
          <Icon icon="fas fa-image" />
        </span>
      </div>
    </div>

    <div class="ask-message-body">
      <HTMLContent
        class="ask-message-text"
        v-html="safeHtml"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

import { HTMLContent, Icon } from '@stakeui';
import AskIcon from './AskIcon.vue';

import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { formatDate } from '@frontend/shared/Util.js';
import { stripCitationMarkers } from '@frontend/ui/features/chat/citations';

const advisorStore = useAdvisorStore();

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  format: {
    type: String,
    default: 'html',
  },
});

const escapeHtml = s =>
  String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
const SANITIZE_OPTIONS = { ADD_ATTR: ['target', 'rel'] };

const safeHtml = computed(() => {
  // Messages stored before the server resolved citations still carry raw markers.
  const text = stripCitationMarkers(String(props.message?.text ?? ''));

  if (props.format !== 'markdown') {
    // normal handling for html/plain
    if (props.format === 'html') return DOMPurify.sanitize(text, SANITIZE_OPTIONS);
    return DOMPurify.sanitize(escapeHtml(text).replace(/\n/g, '<br>'), SANITIZE_OPTIONS);
  }

  return DOMPurify.sanitize(marked.parse(text), SANITIZE_OPTIONS);
});

const componentClasses = computed(() => [
  'ask-message',
  props.message.role && `role-${props.message.role}`,
]);
</script>

<style scoped>
.ask-message {
  align-items: start;
  display: flex;
  gap: 1em;

  &.role-assistant {
    --bg: transparent;
    --fg: var(--stk-text-strong);

    .ask-message-text {
      width: 100%;
    }
  }

  &.role-user {
    --bg: #FDE9E1;
    --fg: #DB513D;

    .ask-message-avatar {
      order: 2;
    }

    .ask-message-body {
      justify-content: end;
    }

    .ask-message-text {
      max-width: 80%;
      padding-left: 0.75em;
      padding-right: 0.75em;
    }
  }
}

.ask-message-avatar {
  --d: 36px;
}

.ask-message-avatar,
.ask-user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
}

.ask-message-avatar,
.ask-user-avatar,
.ask-user-avatar img {
  flex-shrink: 0;
  height: var(--d);
  width: var(--d);
}

.ask-message-body {
  display: flex;
  flex-grow: 1;
}

.ask-message-text {
  background-color: var(--bg);
  border-radius: 0.5em;
  color: var(--fg);
  padding-bottom: 0.375em;
  padding-top: 0.375em;
}

/* Help center citation links, rendered into the message text by the server. */
.ask-message-text :deep(.ask-citation) {
  font-size: 0.7em;
  margin-left: 0.15em;
}

.ask-message-text :deep(.ask-citation a) {
  background-color: rgb(0 0 0 / 7%);
  border-radius: 1em;
  color: inherit;
  padding: 0.15em 0.4em;
  text-decoration: none;
}

.ask-message-text :deep(.ask-citation a:hover) {
  background-color: rgb(0 0 0 / 14%);
}
</style>
